/***/
/**
 * Web library for the yardstick plugin.
 *
 * @packageDocumentation
 */

import {
  configApiRef,
  createApiFactory,
  createComponentExtension,
  createPlugin,
  discoveryApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { yardstickApiRef, YardstickClient } from './api';
import { pdpLoggerApiRef } from '@internal/plugin-pdp-logger-frontend';
import { GATED_MEASUREMENTS } from './feature-flags/gated-measurements';
import { SCA_REMEDIATION_FLAG_KEY } from './feature-flags/sca-remediation';
import { INSIGHTS_REFRESH_FLAG_KEY } from './feature-flags/insights-refresh';
import { ZERO_DAY_FLAG_KEY } from './feature-flags/zero-day';

// In this package you might for example export components or hooks
// that are useful to other plugins or modules.

export { yardstickApiRef } from './api';
export { DATA_GOVERNANCE_TAGS_FLAG } from './feature-flags/gated-measurements';

export const yardstickPlugin = createPlugin({
  id: 'yardstick',
  apis: [
    createApiFactory({
      api: yardstickApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        identityApi: identityApiRef,
        configApi: configApiRef,
        pdpLoggerApi: pdpLoggerApiRef,
      },
      factory: ({ discoveryApi, identityApi, configApi, pdpLoggerApi }) =>
        new YardstickClient({
          discoveryApi,
          identityApi,
          configApi,
          pdpLoggerApi,
        }),
    }),
  ],
  // Statically register a per-user feature flag for every gated measurement so
  // the flags appear in `/settings/feature-flags` at app bootstrap (OFF by
  // default for all users, including admins). The static gating map is the
  // single source of truth for both registration here and UI filtering.
  featureFlags: [
    ...GATED_MEASUREMENTS.map(measurement => ({
      name: measurement.flagKey,
    })),
    // UI feature flag for zero-day surfacing in the entity view actions table.
    // Gate all zero-day UI changes behind this flag until broad rollout.
    { name: ZERO_DAY_FLAG_KEY },
    // Gates the automated remediation UI for Endor SCA findings only. See
    // ./feature-flags/sca-remediation.ts.
    { name: SCA_REMEDIATION_FLAG_KEY },
    // Gates the redesigned Insights page at /insights-refresh. See
    // ./feature-flags/insights-refresh.ts.
    { name: INSIGHTS_REFRESH_FLAG_KEY },
  ],
});

export const ActionsDashboard = yardstickPlugin.provide(
  createComponentExtension({
    name: 'ActionsDashboard',
    component: {
      lazy: () =>
        import('./InsightsAndActions/actions-dashboard').then(
          m => m.ActionsDashboard,
        ),
    },
  }),
);

export const InsightsDashboard = yardstickPlugin.provide(
  createComponentExtension({
    name: 'InsightsDashboard',
    component: {
      lazy: () =>
        import('./InsightsAndActions/insights-dashboard').then(
          m => m.InsightsDashboard,
        ),
    },
  }),
);

export { INSIGHTS_REFRESH_FLAG_KEY } from './feature-flags/insights-refresh';

export const InsightsRefresh = yardstickPlugin.provide(
  createComponentExtension({
    name: 'InsightsRefresh',
    component: {
      lazy: () =>
        import('./InsightsAndActions/insights-refresh').then(
          m => m.InsightsRefresh,
        ),
    },
  }),
);

export const GitHubSimulator = yardstickPlugin.provide(
  createComponentExtension({
    name: 'GitHubSimulator',
    component: {
      lazy: () =>
        import('./components/GitHubSimulator').then(m => m.GitHubSimulator),
    },
  }),
);

export const EntityYardstickPage = yardstickPlugin.provide(
  createComponentExtension({
    name: 'EntityYardstickPage',
    component: {
      lazy: () =>
        import('./components/EntityYardstickPage').then(
          m => m.EntityYardstickPage,
        ),
    },
  }),
);

export { InsightsIssueAction } from './components/insights/modal/modal-dialog';
export type { InsightsIssueActionProps } from './components/insights/modal/modal-dialog';
export { buildInsightsIssueActionProps } from './components/data-table/utils/renders';
